import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminFilterBar } from "@/components/admin/AdminFilterBar";
import {
  getAnalyticsOverview,
  getUserMetrics,
  getRevenueMetrics,
  getSubscriptionBreakdown,
  getAIUsageByFeature,
  getRetentionCohorts,
  exportReport,
  type ExportReportType,
} from "@/services/admin-api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ROLE_HIERARCHY,
  DEFAULT_ANALYTICS_DAYS,
  DEFAULT_RETENTION_WEEKS,
  ANALYTICS_DAY_RANGE_OPTIONS,
} from "@atsready/shared";
import { getSignupSources } from "@/services/analytics";
import { useToast } from "@/hooks/use-toast";
import { AnalyticsOverviewCards } from "@/components/analytics/AnalyticsOverviewCards";
import { UserGrowthChart } from "@/components/analytics/UserGrowthChart";
import { SignupSourcesCard } from "@/components/analytics/SignupSourcesCard";
import { AIUsageChart } from "@/components/analytics/AIUsageChart";
import { RevenueChart } from "@/components/analytics/RevenueChart";
import { ExportDataPanel } from "@/components/analytics/ExportDataPanel";

export default function Analytics() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [days, setDays] = useState(DEFAULT_ANALYTICS_DAYS);
  const [exportStartDate, setExportStartDate] = useState("");
  const [exportEndDate, setExportEndDate] = useState("");
  const [exportType, setExportType] = useState<ExportReportType>("users");
  const [isExporting, setIsExporting] = useState(false);

  const canViewRevenue = !!(user && ROLE_HIERARCHY[user.role] >= ROLE_HIERARCHY["ADMIN"]);

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ["analytics-overview"],
    queryFn: getAnalyticsOverview,
  });

  const { data: userMetrics } = useQuery({
    queryKey: ["user-metrics", days],
    queryFn: () => getUserMetrics(days),
  });

  // The card renders its own error and Retry, so the global toast would only
  // say the same thing twice (`main.tsx` reads this key).
  const signupSources = useQuery({
    queryKey: ["signup-sources", days],
    queryFn: () => getSignupSources(days),
    meta: { skipGlobalErrorHandler: true },
  });

  const { data: revenueMetrics } = useQuery({
    queryKey: ["revenue-metrics", days],
    queryFn: () => getRevenueMetrics(days),
    enabled: canViewRevenue,
  });

  const { data: subscriptionBreakdown } = useQuery({
    queryKey: ["subscription-breakdown"],
    queryFn: getSubscriptionBreakdown,
  });

  const { data: aiUsage } = useQuery({
    queryKey: ["ai-usage", days],
    queryFn: () => getAIUsageByFeature(days),
  });

  const { data: retentionCohorts } = useQuery({
    queryKey: ["retention-cohorts"],
    queryFn: () => getRetentionCohorts(DEFAULT_RETENTION_WEEKS),
  });

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await exportReport({
        type: exportType,
        startDate: exportStartDate || undefined,
        endDate: exportEndDate || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${exportType}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: "Export successful", description: `Downloaded ${exportType}.csv` });
    } catch (error) {
      toast({
        title: "Export failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader title="Analytics" description="Detailed analytics and reporting" />

      <AdminFilterBar
        actions={
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="rounded-md border bg-background px-3 py-2 text-sm"
            >
              {ANALYTICS_DAY_RANGE_OPTIONS.map((value) => (
                <option key={value} value={value}>{`Last ${String(value)} days`}</option>
              ))}
            </select>
          </div>
        }
      >
        <span className="text-sm text-muted-foreground">Time range</span>
      </AdminFilterBar>

      <AnalyticsOverviewCards
        stats={overview?.data}
        isLoading={overviewLoading}
        canViewRevenue={canViewRevenue}
      />

      <UserGrowthChart
        userMetrics={userMetrics}
        subscriptionBreakdown={subscriptionBreakdown}
        signupSourcesCard={
          <SignupSourcesCard
            days={days}
            data={signupSources.data?.data}
            isLoading={signupSources.isLoading}
            error={signupSources.error}
            onRetry={() => void signupSources.refetch()}
          />
        }
      />

      <AIUsageChart userMetrics={userMetrics} aiUsage={aiUsage} />

      <RevenueChart
        revenueMetrics={revenueMetrics}
        canViewRevenue={canViewRevenue}
        retentionCohorts={retentionCohorts}
      />

      <ExportDataPanel
        exportType={exportType}
        exportStartDate={exportStartDate}
        exportEndDate={exportEndDate}
        isExporting={isExporting}
        onExportTypeChange={setExportType}
        onStartDateChange={setExportStartDate}
        onEndDateChange={setExportEndDate}
        onExport={handleExport}
      />
    </div>
  );
}
